import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Star, Download, ArrowLeft } from "lucide-react";
import { listHeallexaReviews } from "@/lib/heallexa-review-admin.functions";
import { useAuth } from "@/hooks/useAuth";

export const Route = createFileRoute("/dashboard/admin/reviews")({
  component: AdminReviewsPage,
});

type Row = {
  id: string;
  provider_id: string;
  provider_name: string | null;
  provider_email: string | null;
  provider_phone: string | null;
  event_type: string;
  rating: number | null;
  feedback_text: string | null;
  google_review_redirected: boolean;
  submitted_at: string | null;
  email_sent_at: string | null;
  sms_sent_at: string | null;
  created_at: string;
};

function AdminReviewsPage() {
  const { user, loading, primaryRole } = useAuth();
  const navigate = useNavigate();
  const list = useServerFn(listHeallexaReviews);

  const [rating, setRating] = useState<number | "">("");
  const [eventType, setEventType] = useState<"" | "claimed_profile" | "appointment_booking">("");
  const [sinceDays, setSinceDays] = useState<number | "">("");
  const [rows, setRows] = useState<Row[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate({ to: "/login" }); return; }
    if (primaryRole && primaryRole !== "admin") { navigate({ to: "/dashboard" }); return; }
    void reload();
  }, [user, loading, primaryRole]);

  const reload = async () => {
    setBusy(true);
    try {
      const res = await list({
        data: {
          rating: rating === "" ? undefined : Number(rating),
          eventType: eventType || undefined,
          sinceDays: sinceDays === "" ? undefined : Number(sinceDays),
          limit: 1000,
        },
      });
      setRows(res.rows as Row[]);
      setStats(res.stats);
    } finally {
      setBusy(false);
    }
  };

  const exportCsv = () => {
    const headers = [
      "id","provider_id","provider_name","provider_email","provider_phone",
      "event_type","rating","feedback_text","google_review_redirected",
      "email_sent_at","sms_sent_at","submitted_at","created_at",
    ];
    const csv = [
      headers.join(","),
      ...rows.map((r) =>
        headers.map((h) => {
          const v = (r as any)[h];
          if (v == null) return "";
          const s = String(v).replace(/"/g, '""');
          return /[",\n]/.test(s) ? `"${s}"` : s;
        }).join(",")
      ),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `heallexa-reviews-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const lowFeedback = useMemo(() => rows.filter((r) => r.rating && r.rating <= 3 && r.feedback_text), [rows]);
  const maxCount = stats ? Math.max(1, ...Object.values(stats.ratingCounts as Record<number, number>)) : 1;

  if (loading || !user) return (
    <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>
  );

  return (
    <div>

      <main className="pt-24 pb-16 mx-auto max-w-7xl px-6">
        <Link to="/dashboard/admin" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Admin
        </Link>
        <div className="mt-3 flex items-center justify-between gap-4">
          <h1 className="text-3xl font-semibold tracking-tight">Review Survey Feedback</h1>
          <Button variant="outline" size="sm" onClick={exportCsv} disabled={!rows.length}>
            <Download className="h-4 w-4" /> Export CSV
          </Button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-3">
            <Stat label="Requests sent" value={stats.totalSent} />
            <Stat label="Completions" value={stats.completions} />
            <Stat label="Completion rate" value={`${Math.round(stats.completionRate * 100)}%`} />
            <Stat label="Average rating" value={stats.averageRating ? stats.averageRating.toFixed(2) : "—"} />
            <Stat label="Google review redirects" value={stats.redirectedToGoogle} />
          </div>
        )}

        {/* Rating distribution */}
        {stats && (
          <div className="mt-6 border border-border bg-card p-5">
            <h2 className="text-sm font-medium mb-3">Rating distribution</h2>
            <div className="space-y-2">
              {[5, 4, 3, 2, 1].map((n) => {
                const c = stats.ratingCounts[n] ?? 0;
                const pct = (c / maxCount) * 100;
                return (
                  <div key={n} className="flex items-center gap-3 text-sm">
                    <div className="flex items-center gap-1 w-12">{n}<Star className="h-3.5 w-3.5 fill-primary text-primary" /></div>
                    <div className="flex-1 h-2 bg-muted rounded">
                      <div className="h-full bg-primary rounded" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="w-10 text-right text-muted-foreground">{c}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="mt-6 flex flex-wrap items-end gap-3 border border-border bg-card p-4">
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Rating</label>
            <select className="h-9 px-2 border border-border bg-background rounded text-sm" value={rating} onChange={(e) => setRating(e.target.value ? Number(e.target.value) : "")}>
              <option value="">All</option>
              {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n} stars</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Event type</label>
            <select className="h-9 px-2 border border-border bg-background rounded text-sm" value={eventType} onChange={(e) => setEventType(e.target.value as any)}>
              <option value="">All</option>
              <option value="claimed_profile">Claimed profile</option>
              <option value="appointment_booking">Appointment booking</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Within last (days)</label>
            <Input type="number" className="h-9 w-32" value={sinceDays} onChange={(e) => setSinceDays(e.target.value ? Number(e.target.value) : "")} placeholder="e.g. 30" />
          </div>
          <Button onClick={reload} disabled={busy} size="sm">{busy ? "Loading…" : "Apply"}</Button>
        </div>

        {/* Low rating feedback */}
        {lowFeedback.length > 0 && (
          <div className="mt-6 border border-border bg-card p-5">
            <h2 className="text-sm font-medium mb-3">Low-rating feedback ({lowFeedback.length})</h2>
            <div className="space-y-3">
              {lowFeedback.map((r) => (
                <div key={r.id} className="border border-border/60 p-3 rounded">
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{r.provider_name ?? r.provider_email ?? "Unknown"} · {r.rating}★ · {r.event_type}</span>
                    <span>{r.submitted_at ? new Date(r.submitted_at).toLocaleString() : ""}</span>
                  </div>
                  <p className="mt-1 text-sm whitespace-pre-wrap">{r.feedback_text}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Table */}
        <div className="mt-6 border border-border bg-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border bg-muted/40">
              <tr className="text-left">
                <th className="p-3 font-medium">Provider</th>
                <th className="p-3 font-medium">Email / Phone</th>
                <th className="p-3 font-medium">Event</th>
                <th className="p-3 font-medium">Rating</th>
                <th className="p-3 font-medium">Feedback</th>
                <th className="p-3 font-medium">Submitted</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={6} className="p-10 text-center text-muted-foreground">No review requests yet.</td></tr>
              ) : rows.map((r) => (
                <tr key={r.id} className="border-b border-border/60">
                  <td className="p-3">{r.provider_name ?? "—"}</td>
                  <td className="p-3 text-muted-foreground text-xs">
                    <div>{r.provider_email ?? "—"}</div>
                    <div>{r.provider_phone ?? ""}</div>
                  </td>
                  <td className="p-3 text-xs">{r.event_type}</td>
                  <td className="p-3">{r.rating ? `${r.rating}★` : "—"}</td>
                  <td className="p-3 max-w-xs truncate" title={r.feedback_text ?? ""}>{r.feedback_text ?? "—"}</td>
                  <td className="p-3 text-xs text-muted-foreground">{r.submitted_at ? new Date(r.submitted_at).toLocaleString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="border border-border bg-card p-4">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-1 text-2xl font-semibold">{value}</div>
    </div>
  );
}
